import { LoaderFunctionArgs, SerializeFrom } from '@remix-run/node'
import { useLoaderData, Outlet, useOutletContext } from '@remix-run/react'
import type { MetaFunction } from '@remix-run/node'
import { typedjson } from 'remix-typedjson'
import jsonld from 'jsonld'
import { GetUserUseCase } from '~/domain/user/usecases/GetUserUseCase'
import { UserDTO } from '~/domain/user/dto/UserDTO'
import { FrontContext } from '~/routes/_front'
import { UserLayout } from '~/domain/user/components/UserLayout/UserLayout'
import { getMeta } from '~/application/services/metaService'

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) {
    return []
  }

  return getMeta({
    title: data.user?.name ?? '',
    url: location.pathname,
    schema: data.schema,
  })
}

export interface UserContext extends FrontContext {
  user?: SerializeFrom<UserDTO>
}

export async function loader({ params }: LoaderFunctionArgs) {
  const id = Number(params.id)
  const getUserUseCase = GetUserUseCase.create()
  const result = await getUserUseCase.execute(id)

  if (result.isErr()) {
    throw new Response('Not Found', { status: 404 })
  }

  const user = result.value
  const schema = await jsonld.compact(
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: user.name,
      url: `https://beblooming.jp/users/${user.id}`,
    },
    {},
  )

  return typedjson({
    user: result.value,
    schema,
  })
}

export default function UserIndex() {
  const data = useLoaderData<typeof loader>()
  const outletContext = useOutletContext<FrontContext>()
  const context: UserContext = {
    ...outletContext,
    user: data.user,
  }

  if (!data.user) {
    return <Outlet />
  }

  return (
    <UserLayout user={data.user} loginUser={context.loginUser}>
      <Outlet context={context} />
    </UserLayout>
  )
}
